import { AsyncPipe, NgClass } from "@angular/common";
import {
    AfterViewInit,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import {
    merge,
    Observable,
    Subject,
    switchMap,
    tap
} from "rxjs";
import { Planeringsstatus, SkogsvardPlanteringPlantskyddMetoder, TradslagExternkod } from "~app/common/models/constants";
import { TradslagDTO } from "~app/common/models/tradslagsdata/TradslagDTO";
import { RuleErrorContext, RuleSeverityLevel, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { MathUtils } from "~app/common/utils/math-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AtgardSkogsvardTradslagPlanta, VKAtgardSkogsvardTradslagPlantorDTO } from "~app/features/virkeskop/models/skogsvard/skogsvard-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { SkogsvardTradslagService } from "~app/features/virkeskop/services/skogsvard-tradslag.service";
import { VirkeskopValidationService } from "~app/features/virkeskop/services/validation/virkeskop-validation.service";
import { arealandelCalculation, malPlHaCalculation, plantAntalCalculation } from "./skogsvard-tradslag-utils";

@Component({
    selector: "rask-skogsvard-tradslag-plantor-row",
    templateUrl: "./skogsvard-tradslag-plantor-row.component.html",
    styleUrls: ["./skogsvard-tradslag-plantor-row.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SimpleSelectorComponent,
        FormsModule,
        NgClass,
        InputNumberComponent,
        IconButtonComponent,
        ButtonComponent,
        NgbTooltip,
        AsyncPipe
    ]
})
export class SkogsvardTradslagPlantorRowComponent implements OnChanges, AfterViewInit, OnInit {
  @Input() allatradslag: TradslagDTO[];
  @Output() deleted = new EventEmitter<AtgardSkogsvardTradslagPlanta>();
  @Input() kontraktReadonly = false;
  @Input() planttyper: Map<number, string[]>;
  @Input() skogsvardTradslagPlanta: AtgardSkogsvardTradslagPlanta;
  @Input() skogsvardTradslagPlantaLista: AtgardSkogsvardTradslagPlanta[];

  protected aktuellaPlanttyper: string[] = [];
  protected allowedPlantskydd = [
      SkogsvardPlanteringPlantskyddMetoder.ja,
      SkogsvardPlanteringPlantskyddMetoder.nej
  ];
  protected isLoading = true;
  protected skogsvardsAtgard$: Observable<Atgard>;
  protected valttradslag: TradslagDTO;

  private readonly atgardMediatorService = inject(AtgardMediatorService);

  private readonly skogsvardTradslagService = inject(SkogsvardTradslagService);
  private readonly updateSrc = new Subject<void>();
  private readonly virkeskopValidationService = inject(VirkeskopValidationService);

  private throttle: any;

  protected get recalculateDisabled() {
      const nettoareal = this.atgardMediatorService.atgard.nettoareal;
      const {
          malPlantorHA, plantantal, arealandel
      } = this.skogsvardTradslagPlanta;
      const values = [
          malPlantorHA,
          plantantal,
          arealandel
      ]
          .filter(v => ObjectUtils.hasValue(v));

      if (!nettoareal) {
          return true;
      }

      return values.length !== 2;
  }

  ngAfterViewInit(): void {
      window.requestAnimationFrame(() => {
          this.isLoading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (this.allatradslag && this.skogsvardTradslagPlanta) {
          this.valttradslag = this.allatradslag.find(t => t.id === this.skogsvardTradslagPlanta.tradslagId);
      }
  }

  ngOnInit(): void {
      this.aktuellaPlanttyper = this.getPlanttyper();

      const update$ = this.updateSrc.pipe(
          switchMap(() => this.atgardMediatorService.atgard$)
      );

      this.skogsvardsAtgard$ = merge(
          this.atgardMediatorService.atgardUpdate$,
          this.atgardMediatorService.atgard$,
          update$
      ).pipe(
          tap((atgard) => this.updateClientSideValidation(atgard))
      );
  }

  protected applyCalculation() {
      const calc = this.calculateExpected();
      this.skogsvardTradslagPlanta = {
          ...this.skogsvardTradslagPlanta,
          malPlantorHA: this.skogsvardTradslagPlanta.malPlantorHA ?? calc.malPlantorHA,
          plantantal: this.skogsvardTradslagPlanta.plantantal ?? calc.plantantal,
          arealandel: this.skogsvardTradslagPlanta.arealandel ?? calc.arealandel
      };
      this.update();
  }

  protected delete(): void {
      if (this.kontraktReadonly) {
          return;
      }
      this.deleted.emit(this.skogsvardTradslagPlanta);
  }

  protected handlesConniflex(): boolean {
      // I skrivande stund är det samma regler för Planttyp som Conniflex
      return this.planttypRequired();
  }

  protected hasDeviatingValues(skogsvardsAtgard: Atgard) {
      const calc = this.calculateExpected();
      if (skogsvardsAtgard.status !== Planeringsstatus.Tillgangligt || skogsvardsAtgard.isReadonly) {
          return false;
      }
      const threshold = 0.1;
      return MathUtils.increase(this.skogsvardTradslagPlanta.plantantal, calc.plantantal) > threshold;
  }

  protected planttypChanged($event) {
      if (!this.isLoading && !_.isNil($event)) {
          clearTimeout(this.throttle);
          this.throttle = setTimeout(() => {
              this.skogsvardTradslagPlanta.plantskydd = null;
              this.update();
          }, 50);
      }
  }

  protected planttypRequired(): boolean {
      switch (this.valttradslag?.externkod) {
          case TradslagExternkod.Tall:
          case TradslagExternkod.Gran:
          case TradslagExternkod.Contorta:
              return true;
      }
      return false;
  }

  protected somethingChanged($event): void {
      this.update();
  }

  protected tradslagChanged($event) {
      if (!this.isLoading && !_.isNil($event) && $event?.id !== this.skogsvardTradslagPlanta?.tradslagId) {
          this.skogsvardTradslagPlanta.tradslagId = $event.id;
          this.skogsvardTradslagPlanta.planttyp = null;
          this.skogsvardTradslagPlanta.plantskydd = null;
          this.aktuellaPlanttyper = this.getPlanttyper();
          this.update();
      }
  }

  protected viewFormatter = (td: TradslagDTO): string => td != null ? td.externkod : "";

  private getPlanttyper(): string[] {
      return this.planttyper?.[this.skogsvardTradslagPlanta.tradslagId] ?? this.planttyper?.[-1];
  }

  private calculateExpected(): CalculationResult {
      const {
          malPlantorHA, plantantal, arealandel
      } = this.skogsvardTradslagPlanta;
      const nettoareal = this.atgardMediatorService.atgard.nettoareal;

      return {
          malPlantorHA: malPlHaCalculation(plantantal, arealandel, nettoareal),
          plantantal: plantAntalCalculation(malPlantorHA, arealandel, nettoareal),
          arealandel: arealandelCalculation(malPlantorHA, plantantal, nettoareal)
      };
  }

  private update(): void {
      this.updateSrc.next();
      this.skogsvardTradslagService.updateSkogsvardTradslag(this.skogsvardTradslagPlanta);
  }

  private updateClientSideValidation(skogsvardsAtgard: Atgard) {
      if (this.valttradslag) {
          this.virkeskopValidationService.updateClientSideValidation(
              this.hasDeviatingValues(skogsvardsAtgard),
              {
                  message: `Totalt antal plantor för ${this.valttradslag.namn.toLowerCase()} diffar mer än 10% från beräknat värde`,
                  severity: RuleSeverityLevel.warning,
                  strategy: RuleVisibilityStrategy.alwaysShow,
                  entityType: "skogsvardsatgard",
                  messageId: `plantDiff_${this.skogsvardTradslagPlanta.tradslagId}`,
                  entityGuids: [skogsvardsAtgard.guid],
                  context: RuleErrorContext.save
              }
          );
      }

      this.virkeskopValidationService.updateClientSideValidation(
          this.skogsvardTradslagPlanta.arealandel > 100,
          {
              message: "Arealandelen överskrider 100%",
              severity: RuleSeverityLevel.warning,
              strategy: RuleVisibilityStrategy.alwaysShow,
              entityType: "skogsvardsatgard",
              messageId: "arealandelOver100",
              entityGuids: [skogsvardsAtgard.guid],
              context: RuleErrorContext.save
          }
      );
  }
}

type CalculationResult = Pick<VKAtgardSkogsvardTradslagPlantorDTO, "arealandel" | "malPlantorHA" | "plantantal">;
